import React, { useEffect, useState } from 'react';
import { Instagram, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const {
    lang,
    setLang,
    t
  } = useLanguage();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [{
    label: t('nav.home'),
    href: '/#inicio'
  }, {
    label: t('nav.what'),
    href: '/#que-son'
  }, {
    label: t('nav.where'),
    href: '/#donde'
  }, {
    label: t('nav.retreats'),
    href: '/#retiros'
  }, {
    label: t('nav.about'),
    href: '/#sobre-mi'
  }, {
    label: t('nav.contact'),
    href: '/#contacto'
  }];
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled || isMobileMenuOpen ? 'py-3 shadow-md' : 'py-4'}`} style={{
    background: 'rgba(247, 241, 227, 0.96)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    borderBottom: '1px solid rgba(26, 58, 42, 0.08)'
  }} data-id="element-383">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center gap-4" data-id="element-384">
        {/* Logo (left) */}
        <motion.a href="/" initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="z-50 flex items-center gap-3 flex-shrink-0" data-id="element-385">
          <img src="https://cdn.magicpatterns.com/uploads/wfsMBWbYUUEtDNTW5oMDav/logo_constelaciones.png" alt="Georgina Constelaciones Familiares" className={`transition-all duration-500 ${isScrolled ? 'h-[48px] md:h-[84px]' : 'h-[56px] md:h-[96px]'} w-auto`} style={{
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.08))'
        }} data-id="element-386" />
        </motion.a>

        {/* Desktop Nav (centered, single line) */}
        <nav className="hidden lg:flex flex-1 items-center justify-center gap-4 lg:gap-4 xl:gap-5 text-[9px] lg:text-[10px] uppercase tracking-[0.14em] lg:tracking-[0.18em] font-medium text-forest-dark" data-id="element-387">
          {navLinks.map((link, i) => <a key={i} href={link.href} className="hover:text-gold-dark transition-colors whitespace-nowrap" data-id="element-388">
              {link.label}
            </a>)}
        </nav>

        {/* Right cluster — Instagram + Language */}
        <div className="hidden lg:flex items-center gap-3 xl:gap-4 flex-shrink-0" data-id="element-389">
          <a href="https://www.instagram.com/georgina_constelaciones/" target="_blank" rel="noopener noreferrer" className="text-forest-dark hover:text-gold-dark transition-colors" data-id="element-390">
            <Instagram className="w-5 h-5" data-id="element-391" />
          </a>

          <div className="w-px h-6 bg-forest-dark/15" data-id="element-392"></div>

          <div className="flex items-center rounded-full p-1 border border-forest-dark/20 bg-white/50" data-id="element-393">
            <button onClick={() => setLang('ca')} className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${lang === 'ca' ? 'bg-gold text-forest-dark border border-gold' : 'text-forest-dark hover:bg-forest-dark/5 border border-transparent'}`} data-id="element-394">
              CA
            </button>
            <button onClick={() => setLang('es')} className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${lang === 'es' ? 'bg-gold text-forest-dark border border-gold' : 'text-forest-dark hover:bg-forest-dark/5 border border-transparent'}`} data-id="element-395">
              ES
            </button>
          </div>
        </div>

        {/* Mobile Toggle */}
        <div className="lg:hidden flex flex-1 justify-end items-center gap-2 sm:gap-3 z-50" data-id="element-396">
          <div className="flex items-center rounded-full p-0.5 sm:p-1 border border-forest-dark/20 bg-white/50" data-id="element-397">
            <button onClick={() => setLang('ca')} className={`px-2 py-0.5 sm:py-1 text-[10px] font-medium rounded-full transition-all ${lang === 'ca' ? 'bg-gold text-forest-dark' : 'text-forest-dark'}`} data-id="element-398">
              CA
            </button>
            <button onClick={() => setLang('es')} className={`px-2 py-0.5 sm:py-1 text-[10px] font-medium rounded-full transition-all ${lang === 'es' ? 'bg-gold text-forest-dark' : 'text-forest-dark'}`} data-id="element-399">
              ES
            </button>
          </div>
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-forest-dark p-2 flex items-center justify-center min-w-[44px] min-h-[44px]" aria-label="Menú" data-id="element-400">
            {isMobileMenuOpen ? <X className="w-6 h-6" data-id="element-401" /> : <Menu className="w-6 h-6" data-id="element-402" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-403">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="lg:hidden border-t border-forest-dark/10 overflow-hidden" style={{
        background: 'rgba(247, 241, 227, 0.98)',
        backdropFilter: 'blur(10px)'
      }} data-id="element-404">
            <nav className="flex flex-col items-center py-8 px-6 gap-5 text-sm uppercase tracking-widest font-medium text-forest-dark text-center" data-id="element-405">
              {navLinks.map((link, i) => <a key={i} href={link.href} onClick={() => setIsMobileMenuOpen(false)} className="hover:text-gold-dark transition-colors leading-snug max-w-[280px]" data-id="element-406">
                  {link.label}
                </a>)}
              <div className="w-12 h-[1px] bg-forest-dark/15 my-2" data-id="element-407"></div>
              <a href="https://www.instagram.com/georgina_constelaciones/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-5 py-2.5 border border-gold/50 rounded-full text-gold-dark hover:bg-gold/10 transition-colors" data-id="element-408">
                <Instagram className="w-5 h-5" data-id="element-409" />
                <span className="text-xs" data-id="element-410">@georgina_constelaciones</span>
              </a>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </header>;
}